/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type Command, FormControl, FormGroup, FormSlideWindowAbstract, UtilService } from '@dm/core';
import { type CIFSClient, type CIFSShare, type FileSystem } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';

import { type CIFSClientVm } from '../../../share';
import { ShareTypeE } from '../../../share.enum';
import { CifsShareStrService } from '../../cifs-share-str.service';
import { ClientCifsShareService } from '../client-cifs-share.service';
import { prefixHandler } from '../client-clifs-share';

@Component({
    selector: 'app-modify-client-cifs-share',
    templateUrl: './modify-client-cifs-share.component.html',
    styleUrls: ['./modify-client-cifs-share.component.scss'],
})
export class ModifyClientCifsShareComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 待修改的Client，前后台都要传入
     */
    @Input() data: CIFSClient;

    /**
     * 是否是前台表格的按钮
     */
    @Input() isFrontTable = false;
    /**
     * 前台表格传入的clients，用于校验重复，后端表格下发接口校验
     */
    @Input() clients: CIFSClient[] = [];

    @Input() entry: FileSystemEntryE;
    /**
     * 前台表格完成处理之后，emit数据回去
     */
    @Output() callBack: EventEmitter<CIFSClient> = new EventEmitter();
    /**
     * 后台表格传入的当前要修改的client所属的share
     */
    @Input() cifsShare: CIFSShare;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;

    i18n = this.str.i18n;

    vm: FormGroup;
    errorMessage: any;

    FileSystemEntryE = FileSystemEntryE;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    prefixHandler = prefixHandler;

    get changedRawValue(): CIFSClientVm {
        return this.vm ? this.vm.changedRawValue : null;
    }

    constructor(
        public str: CifsShareStrService,
        private service: ClientCifsShareService,
        public utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        const tempControls = {
            // 权限级别
            permission: new FormControl(Number(this.data.PERMISSION), []),
        };

        this.vm = new FormGroup(tempControls, { updateOn: 'blur' });

        this.errorMessage = {
            // 暂无内容
        };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];
        // 前台表格不需要cmds，只用emit数据回去
        // 先收集数据
        const changeData = this.collectionClient();
        if (this.data.vstoreId) {
            changeData.vstoreId = this.data.vstoreId;
        }
        if (this.isFrontTable) {
            this.callBack.emit(changeData);
        } else {
            cmds.push(this.buildCIFSClientCmd(changeData));
        }
        return cmds;
    }

    collectionClient(): CIFSClient {
        const params: CIFSClient = {};
        const changedValue = this.changedRawValue;
        if (Object.prototype.hasOwnProperty.call(changedValue, 'permission')) {
            params.PERMISSION = changedValue.permission;
        }
        return params;
    }

    buildCIFSClientCmd(changeData: CIFSClient): Command {
        const typeStr = this.str.getUpperCaseUserOrGroupType(this.data);
        return {
            name: this.cifsShare.subType === ShareTypeE.Homedir ? this.i18n.rest.homedir.modifyClient : this.i18n.rest.modifyClient,
            nameParams: [typeStr, prefixHandler(this.data.NAME)],
            action: this.service.modify(this.data.ID, changeData),
        };
    }
}
